import React, { useState, useEffect, useRef, Component, ErrorInfo, ReactNode } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  AccountTree as RelationshipIcon,
  MergeType as MergeIcon,
  CloudUpload as UploadIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import DocumentSetUpload from '../components/DocumentSetUpload/DocumentSetUpload';
import RelationshipBuilder from '../components/RelationshipBuilder/RelationshipBuilder';
import DocumentCombiner from '../components/DocumentCombiner/DocumentCombiner';
import {
  documentSetService,
  DocumentSet,
  Document,
  DocumentRelationship,
  Conflict,
} from '../features/cross-document/documentSetService';
import ModuleAuthModal from '../shared/components/ModuleAuthModal';
import { brand, primaryButtonSx } from '../theme';
import authService from '../shared/services/authService';
import { useTranslation } from '../store/languageStore';

// Error boundary to prevent blank screen if RelationshipBuilder/ForceGraph crashes
class GraphErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean; errorMsg: string }
> {
  state = { hasError: false, errorMsg: '' };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMsg: error?.message || String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('RelationshipBuilder render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <>
          {this.props.fallback}
          <pre style={{ fontSize: 12, color: '#888', margin: 8 }}>
            {this.state.errorMsg}
          </pre>
        </>
      );
    }
    return this.props.children;
  }
}

const DocumentSetManager: React.FC = () => {
  const navigate = useNavigate();
  const { t, tString } = useTranslation();
  const [documentSets, setDocumentSets] = useState<DocumentSet[]>([]);
  const [selectedSet, setSelectedSet] = useState<DocumentSet | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [relationships, setRelationships] = useState<DocumentRelationship[]>([]);
  const [mergedData, setMergedData] = useState<any>(null);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [confidenceScore, setConfidenceScore] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSetId, setMenuSetId] = useState<string | null>(null);
  const [isLoadingSet, setIsLoadingSet] = useState(false);
  const [isCreatingSet, setIsCreatingSet] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [atlasModuleId, setAtlasModuleId] = useState<string | null>(null);
  // Step requested before the selected set finished loading (e.g. card "Submit" shortcut).
  // Resolved with guards in the selectedSet effect below.
  const pendingStepRef = useRef<number | null>(null);

  useEffect(() => {
    loadDocumentSets();
  }, []);

  useEffect(() => {
    const fetchAtlasModuleId = async () => {
      try {
        const modules = await authService.getModules();
        const atlas = modules.find((m: any) => m.name === 'BEO ATLAS');
        if (atlas) setAtlasModuleId(atlas.id);
      } catch (e) {
        console.error('Failed to fetch modules:', e);
      }
    };
    fetchAtlasModuleId();
  }, []);

  // Load documents and reset workflow state when a set is selected
  useEffect(() => {
    if (selectedSet) {
      const docs = selectedSet.documents || [];
      setDocuments(docs);
      // Normalize matching_fields to always be a string array
      setRelationships(
        (selectedSet.relationships || []).map((rel: any) => ({
          ...rel,
          matching_fields: Array.isArray(rel.matching_fields)
            ? rel.matching_fields
            : Object.keys(rel.matching_fields || {}),
        }))
      );
      // Restore persisted combine results if the set was already combined
      const persistedMergedData = selectedSet.merged_data || null;
      setMergedData(persistedMergedData);
      setConflicts(selectedSet.conflicts || []);
      setConfidenceScore(selectedSet.confidence_score || 0);

      // Resolve any requested step (e.g. card "Submit" shortcut), guarding
      // against missing prerequisites from the freshly loaded set
      const requestedStep = pendingStepRef.current ?? 0;
      pendingStepRef.current = null;
      let step = 0;
      if (requestedStep >= 1 && docs.length >= 2) step = 1;
      if (requestedStep >= 2 && docs.length >= 2 && persistedMergedData) step = 2;
      if (requestedStep > step) {
        toast.warning(
          docs.length < 2
            ? 'Upload at least 2 documents first'
            : 'Combine documents first'
        );
      }
      setActiveStep(step);
    } else {
      // Reset combine/workflow state when leaving a set
      pendingStepRef.current = null;
      setMergedData(null);
      setConflicts([]);
      setConfidenceScore(0);
      setActiveStep(0);
    }
  }, [selectedSet]);

  const loadDocumentSets = async () => {
    try {
      setIsInitialLoading(true);
      const sets = await documentSetService.getDocumentSets();
      // Ensure sets is always an array
      setDocumentSets(Array.isArray(sets) ? sets : []);
    } catch (error) {
      console.error('Error loading document sets:', error);
      toast.error('Failed to load document sets');
      // Set empty array on error to prevent map errors
      setDocumentSets([]);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSelectSet = async (set: DocumentSet) => {
    try {
      setIsLoadingSet(true);
      const fullSet = await documentSetService.getDocumentSet(set.id);
      // Transform backend members to frontend Document type
      const docs: Document[] = (fullSet.documents || []).map((m: any) => ({
        id: m.file_id || m.id,
        file_name: m.filename || m.file_name || 'unknown',
        document_type: m.document_type,
        document_type_confidence: m.extraction_confidence || m.document_type_confidence,
        extracted_data: m.extracted_data,
        file_size: m.file_size || 0,
        status: (m.processed_at || m.status === 'completed') ? 'completed' : 'pending',
      }));
      setSelectedSet({ ...fullSet, documents: docs });
    } catch (error) {
      console.error('Error loading document set details:', error);
      toast.error('Failed to load document set details');
      // Fall back to the list-level data
      setSelectedSet(set);
    } finally {
      setIsLoadingSet(false);
    }
  };

  const handleCreateSet = async () => {
    if (isCreatingSet) return;
    if (!newSetName.trim()) {
      toast.error('Please enter a name for the document set');
      return;
    }

    try {
      setIsCreatingSet(true);
      const newSet = await documentSetService.createDocumentSet(newSetName);
      setDocumentSets((prev) => [...prev, newSet]);
      setSelectedSet(newSet);
      setCreateDialogOpen(false);
      setNewSetName('');
      setActiveStep(0);
      toast.success('Document set created successfully');
    } catch (error) {
      toast.error('Failed to create document set');
    } finally {
      setIsCreatingSet(false);
    }
  };

  const handleDeleteSet = async (setId: string) => {
    try {
      await documentSetService.deleteDocumentSet(setId);
      setDocumentSets(documentSets.filter((s) => s.id !== setId));
      if (selectedSet?.id === setId) {
        setSelectedSet(null);
      }
      toast.success('Document set deleted');
    } catch (error) {
      toast.error('Failed to delete document set');
    }
  };

  const handleDocumentsUploaded = async (files: File[]) => {
    if (!selectedSet) return;

    try {
      setIsLoading(true);
      const { succeeded, failed } = await documentSetService.addMultipleDocuments(
        selectedSet.id,
        files,
        (fileIndex, progress) => {
          // Handle progress updates
          console.log(`File ${fileIndex}: ${progress}%`);
        }
      );
      if (succeeded.length > 0) {
        setDocuments((prev) => [...prev, ...succeeded]);
        toast.success(`${succeeded.length} document(s) uploaded successfully`);
      }
      failed.forEach((failure) => {
        toast.error(`Failed to upload ${failure.file_name}: ${failure.error}`);
      });
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (!selectedSet) return;

    try {
      await documentSetService.removeDocument(selectedSet.id, documentId);
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      toast.success('Document removed');
    } catch (error) {
      toast.error('Failed to remove document');
    }
  };

  const handleAutoDetect = async () => {
    if (!selectedSet) return;

    try {
      setIsLoading(true);
      const detectedRelationships = await documentSetService.getRelationships(selectedSet.id);
      setRelationships(detectedRelationships);
      if (detectedRelationships.length > 0) {
        toast.success(`${detectedRelationships.length} relationship(s) detected`);
      } else {
        toast.info('No relationships found between documents');
      }
    } catch (error) {
      toast.error('Failed to detect relationships');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCombineDocuments = async () => {
    if (!selectedSet) return;

    try {
      setIsLoading(true);
      const result = await documentSetService.combineDocuments(selectedSet.id);
      setMergedData(result.merged_data || {});
      setConflicts(result.conflicts || []);
      setConfidenceScore(result.confidence_score || 0);
      setActiveStep(2);
      toast.success('Documents combined successfully');
    } catch (error) {
      toast.error('Failed to combine documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveConflict = (field: string, value: any) => {
    // Update merged data with resolved value
    setMergedData({ ...mergedData, [field]: value });
  };

  const handleSubmitToAtlas = async (testMode: boolean, customReference?: string) => {
    if (!selectedSet) {
      return { success: false, error: 'No document set selected' };
    }

    // Check ATLAS authentication first. If the module lookup failed on page
    // load, surface it instead of silently skipping the auth pre-check.
    if (!atlasModuleId) {
      toast.error('BEO ATLAS module could not be loaded. Please refresh the page and try again.');
      return { success: false, error: 'BEO ATLAS module unavailable' };
    }
    try {
      const authStatus = await authService.checkModuleAuthStatus(atlasModuleId);
      if (!authStatus.authenticated) {
        toast.error('Authentication required for BEO ATLAS. Please authenticate first.');
        setAuthModalOpen(true);
        return { success: false, error: 'Authentication required' };
      }
    } catch (e) {
      // Fall through to submission — the backend 401 handler below still
      // surfaces auth failures and opens the auth modal.
      console.error('Auth check failed:', e);
    }

    try {
      const result = await documentSetService.submitToAtlas(
        selectedSet.id,
        testMode,
        customReference
      );
      if (result.success) {
        toast.success('Successfully submitted to BEO ATLAS');
        await loadDocumentSets();
      } else {
        toast.error(result.error || 'Submission failed');
      }
      return result;
    } catch (error: any) {
      // Handle 401 from backend as auth required
      if (error?.response?.status === 401) {
        toast.error('Authentication required for BEO ATLAS. Please authenticate first.');
        setAuthModalOpen(true);
        return { success: false, error: 'Authentication required' };
      }
      toast.error('Failed to submit to ATLAS');
      return { success: false, error: 'Submission error' };
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, setId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuSetId(setId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSetId(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'submitted':
        return 'success';
      case 'processing':
      case 'ready':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const steps = [
    tString('documentSetManager.steps.upload') || 'Upload Documents',
    tString('documentSetManager.steps.detectRelationships') || 'Detect Relationships',
    tString('documentSetManager.steps.reviewSubmit') || 'Review & Submit',
  ];

  const renderDocumentSetCard = (set: DocumentSet) => (
    <Grid item xs={12} md={6} lg={4} key={set.id}>
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 3,
          },
          ...(selectedSet?.id === set.id && {
            border: `2px solid ${brand.orange}`,
          }),
        }}
        onClick={() => handleSelectSet(set)}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" gutterBottom>
                {set.name}
              </Typography>
              <Box display="flex" gap={1} mb={1}>
                <Chip
                  label={set.status}
                  size="small"
                  color={getStatusColor(set.status)}
                />
                <Chip
                  icon={<DocumentIcon />}
                  label={`${(set as any).document_count ?? set.documents?.length ?? 0} docs`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, set.id);
              }}
            >
              <MoreIcon />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(set.created_at).toLocaleDateString()}
          </Typography>
          {set.submission_id && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                Submission ID: {set.submission_id}
              </Typography>
            </Box>
          )}
          {set.confidence_score !== undefined && (
            <Box mt={1}>
              <LinearProgress
                variant="determinate"
                value={set.confidence_score * 100}
                sx={{ height: 4, borderRadius: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                Confidence: {Math.round(set.confidence_score * 100)}%
              </Typography>
            </Box>
          )}
        </CardContent>
        <CardActions>
          <Button
            size="small"
            startIcon={<ViewIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectSet(set);
            }}
          >
            View
          </Button>
          {set.status === 'ready' && (
            <Button
              size="small"
              startIcon={<SendIcon />}
              color="primary"
              disabled={isLoadingSet}
              onClick={async (e) => {
                e.stopPropagation();
                // Request step 2; the selectedSet effect applies it only when
                // the loaded set actually has documents + merged data
                pendingStepRef.current = 2;
                await handleSelectSet(set);
              }}
            >
              Submit
            </Button>
          )}
        </CardActions>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: brand.textPrimary }}>
            {tString('features.documentSetManager.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {tString('features.documentSetManager.subtitle')}
          </Typography>
        </Box>

        {/* Action Button */}
        <Box display="flex" justifyContent="flex-end" mb={4}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="large"
            sx={primaryButtonSx}
          >
            {tString('documentSets.newDocumentSet')}
          </Button>
        </Box>

      {/* Document Sets Grid */}
      {!selectedSet && (
        <>
          {isInitialLoading ? (
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Loading document sets...</Typography>
            </Card>
          ) : documentSets.length === 0 ? (
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <UploadIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Document Sets
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Create your first document set to start combining trade documents
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={primaryButtonSx}
              >
                Create Document Set
              </Button>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {Array.isArray(documentSets) && documentSets.map(renderDocumentSetCard)}
            </Grid>
          )}
        </>
      )}

      {/* Selected Set Workflow */}
      {selectedSet && (
        <Box>
          {isLoadingSet && <LinearProgress sx={{ mb: 2 }} />}
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <IconButton onClick={() => setSelectedSet(null)}>
              <ViewIcon />
            </IconButton>
            <Typography variant="h5">{selectedSet.name}</Typography>
            <Chip label={selectedSet.status} color={getStatusColor(selectedSet.status)} />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel
                      onClick={() => {
                        if (index > 0 && documents.length < 2) {
                          toast.warning('Upload at least 2 documents first');
                          return;
                        }
                        if (index === 2 && !mergedData) {
                          toast.warning('Combine documents first');
                          return;
                        }
                        setActiveStep(index);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      {label}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="caption">
                        {index === 0 && `${documents.length} documents uploaded`}
                        {index === 1 && `${relationships.length} relationships found`}
                        {index === 2 && 'Ready for submission'}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Grid>

            <Grid item xs={12} md={9}>
              {activeStep === 0 && (
                <Box>
                  <DocumentSetUpload
                    documents={documents}
                    onDocumentsUploaded={handleDocumentsUploaded}
                    onRemoveDocument={handleRemoveDocument}
                    showDocumentTypes
                  />
                  {documents.length >= 2 && (
                    <Box mt={3} textAlign="center">
                      <Button
                        variant="contained"
                        onClick={() => setActiveStep(1)}
                        startIcon={<RelationshipIcon />}
                        sx={primaryButtonSx}
                      >
                        Detect Relationships
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  <GraphErrorBoundary
                    fallback={
                      <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to render relationship graph. Try refreshing the page.
                      </Alert>
                    }
                  >
                    <RelationshipBuilder
                      documents={documents}
                      relationships={relationships}
                      onAutoDetect={handleAutoDetect}
                      isLoading={isLoading}
                    />
                  </GraphErrorBoundary>
                  {relationships.length > 0 && (
                    <Box mt={3} textAlign="center">
                      <Button
                        variant="contained"
                        onClick={handleCombineDocuments}
                        startIcon={<MergeIcon />}
                        disabled={isLoading}
                        sx={primaryButtonSx}
                      >
                        Combine Documents
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {activeStep === 2 && mergedData && (
                <DocumentCombiner
                  documentSet={selectedSet}
                  mergedData={mergedData}
                  conflicts={conflicts}
                  confidenceScore={confidenceScore}
                  onResolveConflict={handleResolveConflict}
                  onSubmitToAtlas={handleSubmitToAtlas}
                  isLoading={isLoading}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create Document Set</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document Set Name"
            fullWidth
            variant="outlined"
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
            placeholder="e.g., Export Shipment 2024-001"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSet}
            variant="contained"
            disabled={isCreatingSet}
            sx={primaryButtonSx}
          >
            {isCreatingSet ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            const set = documentSets.find((s) => s.id === menuSetId);
            if (set) {
              handleSelectSet(set);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuSetId && window.confirm('Delete this document set? This cannot be undone.')) {
              handleDeleteSet(menuSetId);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      {/* ATLAS Auth Modal */}
      {atlasModuleId && (
        <ModuleAuthModal
          open={authModalOpen}
          onClose={(authenticated) => {
            setAuthModalOpen(false);
            if (authenticated) {
              toast.success('BEO ATLAS authentication successful! You can now submit.');
            }
          }}
          moduleId={atlasModuleId}
          moduleName="BEO ATLAS"
        />
      )}
    </Container>
  </Box>
  );
};

export default DocumentSetManager;